import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import React from 'react';

import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import {CapabilitiesFilterCapability as GeneratedCapabilitiesFilterCapability} from 'search/filters/__generated/CapabilitiesFilterCapability';
import DropdownFilterForm, {prefixedToggleText} from 'search/filters/base/DropdownFilterForm';
import {Filter, MultiValueFilter} from 'search/SearchConstants';
import {sortReleasedCapabilitiesForDisplay} from 'tenants/admin/CapabilityManagementUtil';
import {UserListingCapability} from 'tenants/admin/UserListingHeader';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

export const CAPABILITIES_FILTER_CAPABILITY = gql`
  fragment CapabilitiesFilterCapability on CapabilityNode {
    key
    displayName
    isEnabled
  }
`;

export const CapabilitiesFilterFragment: FragmentWrapper<CapabilitiesFilterCapability> = {
  fragmentName: 'CapabilitiesFilterFragment',
  fragment: CAPABILITIES_FILTER_CAPABILITY,
};

export type CapabilitiesFilterCapability = WithoutTypenames<GeneratedCapabilitiesFilterCapability>;

interface CapabilitiesFilterFormProps extends BaseFilterFormProps {
  filter: MultiValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: Filter) => void;
  onRemove?: () => void;
  capabilities: Array<UserListingCapability>;
}

export interface CapabilitiesFilterFormExtraProps {
  capabilities: Array<UserListingCapability>;
}

export const NO_CAPABILITIES_FILTER = {
  name: 'None',
  // Keep in sync with benchling/parties/users/user_search.py
  value: 'NO_CAPABILITIES',
};

export default function CapabilitiesFilterForm(
  props: CapabilitiesFilterFormProps & CapabilitiesFilterFormExtraProps
): JSX.Element {
  const baseProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);

  const releasedCapabilities = sortReleasedCapabilitiesForDisplay(props.capabilities);
  const capabilities = releasedCapabilities.map((capability) => ({
    name: capability.displayName,
    value: capability.key,
  }));
  const optionDatas = compact<OptionData>([NO_CAPABILITIES_FILTER, ...capabilities]);

  return (
    <DropdownFilterForm
      {...baseProps}
      dropdownToggleText={prefixedToggleText('Application access', optionDatas, props.filter)}
      filterStyle={props.filterStyle}
      isMulti={true}
      isSearchable={false}
      onChangeValue={(value: Array<string>) => {
        props.onChangeFilter({...props.filter, value});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      value={props.filter.value}
    />
  );
}
